import {
  Alert, Anchor, Button, Container, Fieldset, Grid, Group, LoadingOverlay,
  NumberInput, Select, Stack, Text, Textarea, TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconEdit, IconRotate, IconX } from '@tabler/icons-react';
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { StickyDetailHeader } from '../components/StickyHeader';
import { useAlertModal } from '../components/AlertModalProvider';

import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import type {
  BeneficiaryDesignation, InsurancePolicyPartyInput, InsurancePolicyPartyRole,
} from '../api/types';
import { PersonPicker } from '../components/EntityPicker';
import { useAccountInsurance } from '../hooks/useAccountInsurance';
import {
  useInsurancePolicyParty, useCreateInsurancePolicyParty,
  useRestoreInsurancePolicyParty,
  useUpdateInsurancePolicyParty,
} from '../hooks/useInsurancePolicyParties';

const ROLE_OPTS: { value: InsurancePolicyPartyRole; label: string }[] = [
  { value: 'POLICY_HOLDER', label: 'Policy Holder' },
  { value: 'LIFE_INSURED',  label: 'Life Insured' },
  { value: 'BENEFICIARY',   label: 'Beneficiary' },
];

const DESIGNATION_OPTS: { value: BeneficiaryDesignation; label: string }[] = [
  { value: 'PRIMARY',     label: 'Primary' },
  { value: 'CONTINGENT',  label: 'Contingent' },
];

interface FormValues {
  policy_id: string;
  person_id: string;
  role: InsurancePolicyPartyRole;
  // Designation/percentage/date fields apply only when role=BENEFICIARY.
  // For POLICY_HOLDER and LIFE_INSURED rows the DB CHECK constraint
  // forces NULL on all three, so we strip them on submit.
  designation: string;            // '' or BeneficiaryDesignation
  percentage_share: number | '';
  designation_date: string;       // ISO date or ''
  notes: string;
}

const EMPTY: FormValues = {
  policy_id: '', person_id: '', role: 'BENEFICIARY',
  designation: 'PRIMARY', percentage_share: '', designation_date: '',
  notes: '',
};

export function InsurancePolicyPartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const { data, isLoading } = useInsurancePolicyParty(isNew ? undefined : id);
  const create = useCreateInsurancePolicyParty();
  const update = useUpdateInsurancePolicyParty(id ?? '');
  const restore = useRestoreInsurancePolicyParty();
  const { showAlert } = useAlertModal();

  const presetPolicyId = isNew ? (searchParams.get('policy_id') ?? '') : '';
  // D2-polish 2 carry-over: when returning from a [+ Create new person] flow,
  // the new person's id arrives as ?person_id=<id>; pre-fill so the user
  // doesn't have to re-pick.
  const presetPersonId = isNew ? (searchParams.get('person_id') ?? '') : '';
  const presetRole = isNew
    ? ((searchParams.get('role') as InsurancePolicyPartyRole | null) ?? 'BENEFICIARY')
    : 'BENEFICIARY';

  const initial: FormValues = isNew || !data ? {
    ...EMPTY,
    policy_id: presetPolicyId,
    person_id: presetPersonId,
    role: presetRole,
    // POLICY_HOLDER / LIFE_INSURED rows must have designation NULL.
    designation: presetRole === 'BENEFICIARY' ? 'PRIMARY' : '',
  } : {
    policy_id: data.policy_id,
    person_id: data.person_id,
    role: data.role,
    designation: data.designation ?? '',
    percentage_share: data.percentage_share ?? '',
    designation_date: data.designation_date ?? '',
    notes: data.notes ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      policy_id: (v) => (v.length >= 32 ? null : 'Policy required'),
      person_id: (v) => (v.length >= 32 ? null : 'Person required'),
      designation: (v, all) =>
        all.role === 'BENEFICIARY' && !v ? 'Required for beneficiaries' : null,
    },
  });
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('insurance-policy-party-form', form.isDirty(), !isNew);
  const confirmDiscard = useDirtyFormGuard(form.isDirty(), saveForGuard);
  const [editMode, setEditMode] = React.useState(isNew);

  // Hydrate the form once the API response lands (mirrors AgreementPartyDetailPage).
  if (!isNew && data && form.values.policy_id === '' && data.policy_id !== '') {
    form.setValues(initial);
    form.resetDirty(initial);
  }

  // Display the policy as "{insurer} - {policy_number}" so the user has
  // unambiguous context for which policy they're editing parties on.
  // Falls back to the raw id while loading.
  const policyId = form.values.policy_id || presetPolicyId;
  const { data: policy } = useAccountInsurance(policyId || undefined);

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;
  const role = form.values.role;

  // When the user toggles role away from BENEFICIARY, blank the
  // beneficiary-only fields so the DB CHECK constraint passes and the
  // submitted payload doesn't carry stale values.
  React.useEffect(() => {
    if (role !== 'BENEFICIARY') {
      if (form.values.designation) form.setFieldValue('designation', '');
      if (form.values.percentage_share !== '') form.setFieldValue('percentage_share', '');
      if (form.values.designation_date) form.setFieldValue('designation_date', '');
    } else if (!form.values.designation) {
      form.setFieldValue('designation', 'PRIMARY');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  function buildPayload(values: FormValues, forCreate: boolean): InsurancePolicyPartyInput {
    const p: InsurancePolicyPartyInput = {};
    if (forCreate) {
      p.policy_id = values.policy_id;
      p.person_id = values.person_id;
      p.role = values.role;
    }
    if (values.role === 'BENEFICIARY') {
      p.designation = (values.designation || 'PRIMARY') as BeneficiaryDesignation;
      p.percentage_share = values.percentage_share === '' ? null : values.percentage_share;
      p.designation_date = values.designation_date || null;
    } else {
      // Explicit nulls so the API patches non-BENEFICIARY rows back to
      // NULL if the user changed the role on an existing row.
      p.designation = null;
      p.percentage_share = null;
      p.designation_date = null;
    }
    if (values.notes) p.notes = values.notes;
    return p;
  }

  function handleSubmit(values: FormValues) {
    if (isNew) {
      const payload = buildPayload(values, true);
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): see AgreementDetailPage
          // create.mutate onSuccess for the rationale. Flip editMode
          // explicitly so the fallback navigate
          // (`/insurance-policy-parties/${id}`) lands in read mode.
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new party assignment was filed. It is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Party added', color: 'green' });
            const returnTo = searchParams.get('return_to');
            if (returnTo) {
              navigate(returnTo, { replace: true });
            } else {
              navigate(`/insurance-policy-parties/${r.entity.id}`, { replace: true });
            }
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    } else {
      const payload = buildPayload(values, false);
      update.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty(values);
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your edits were filed. The live record is unchanged until Compliance approves.',
            });
          } else {
            notifications.show({ message: 'Saved', color: 'green' });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    }
  }

  const titleSuffix = isNew
    ? 'New policy party'
    : `${data?.role ?? '...'} on policy`;

  return (
    <Container size="xl">
      <Stack>
        <StickyDetailHeader
          editing={editMode}
          title={titleSuffix}
          backOnClick={() => confirmDiscard(() => {
            // #391 sweep (2026-06-09): clear dirty state BEFORE navigating
            // so the passive useBlocker (DirtyFormProvider, #258) doesn't
            // re-trigger the discard modal on the navigate.
            form.setValues(initial);
            form.resetDirty(initial);
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            !editMode ? (
              <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      form.setValues(initial);
                      form.resetDirty(initial);
                      setEditMode(false);
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="insurance-policy-party-form" type="submit"
                  loading={create.isPending || update.isPending}
                  disabled={!form.isDirty() && !isNew}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
              </Group>
            )
          }
          destructive={
            /* #8 (2026-06-16, Pedro): role page edits the role only — no
               Delete; break the link from the list. Restore stays for a
               soft-deleted assignment. */
            !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        <form id="insurance-policy-party-form" onSubmit={form.onSubmit(handleSubmit)}>
          <Stack pos="relative">
            <LoadingOverlay visible={isLoading} />
            <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
              <Fieldset legend="Linkage (immutable after creation)">
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Policy</Text>
                    {policy ? (
                      <Anchor component={Link} to={`/account-insurance/${policy.id}`} size="sm">
                        {policy.insurer} — <Text span ff="monospace">{policy.policy_number}</Text>
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed" ff="monospace">{policyId || '—'}</Text>
                    )}
                    {isNew && presetPolicyId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — adding from this policy</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <PersonPicker
                      label="Person" required
                      disabled={!isNew || !!presetPersonId}
                      value={form.values.person_id}
                      onChange={(v) => form.setFieldValue('person_id', v ?? '')}
                      onCreateNew={isNew && !presetPersonId ? () => {
                        const here = window.location.pathname + window.location.search;
                        const target = `/persons/new?return_to=${encodeURIComponent(here)}&return_field=person_id`;
                        navigate(target);
                      } : undefined}
                    />
                    {isNew && presetPersonId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — newly created person</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select label="Role" data={ROLE_OPTS}
                      {...form.getInputProps('role')}
                      disabled={!isNew} allowDeselect={false} />
                    <Text size="xs" c="dimmed" mt={2}>
                      Role is set at creation. To change a row's role, delete + re-create.
                    </Text>
                  </Grid.Col>
                </Grid>
              </Fieldset>

              {role === 'BENEFICIARY' && (
                <Fieldset legend="Beneficiary details">
                  <Grid>
                    <Grid.Col span={4}>
                      <Select label="Designation" data={DESIGNATION_OPTS}
                        {...form.getInputProps('designation')}
                        allowDeselect={false} required />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <NumberInput label="Percentage share" min={0} max={100}
                        decimalScale={2} suffix="%"
                        value={form.values.percentage_share}
                        onChange={(v) => form.setFieldValue('percentage_share', v === '' ? '' : Number(v))}
                      />
                      <Text size="xs" c="dimmed" mt={2}>
                        Sums to 100% across PRIMARY beneficiaries on the policy.
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput label="Designation date" type="date"
                        {...form.getInputProps('designation_date')} />
                    </Grid.Col>
                  </Grid>
                </Fieldset>
              )}

              {role !== 'BENEFICIARY' && (
                <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
                  {role === 'POLICY_HOLDER'
                    ? 'Policy Holder rows do not carry designation, percentage share, or designation date.'
                    : 'Life Insured rows do not carry designation, percentage share, or designation date.'}
                </Alert>
              )}

              <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
            </fieldset>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
